import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { useModalFocus } from '../../lib/useModalFocus.js';
import { ConversationEntry } from './ConversationEntry.js';
import { NewThreadForm } from './NewThreadForm.js';
import { ThreadList } from './ThreadList.js';
import { ThreadView } from './ThreadView.js';
import { ANCHOR_NOUN, useConversationsEnabled, useDiscuss, type DiscussTarget } from './hooks.js';

/**
 * Scene threads of earlier drafts, placed on this scene (spec §6). CERTAIN reads
 * as "from an earlier draft"; LIKELY is offered as a suggestion and never merged
 * into this scene's counts. Nothing is re-anchored.
 */
export function useSceneCarryover(projectId: string) {
  const enabled = useConversationsEnabled();
  const script = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
    enabled: enabled && projectId !== '',
  });
  const versionId = script.data?.scriptVersionId ?? null;
  return useQuery({
    queryKey: ['scene-carryover', projectId, versionId],
    queryFn: () => api.sceneCarryover(projectId, versionId ?? ''),
    enabled: enabled && versionId !== null,
    staleTime: 60_000,
  });
}

/**
 * THE CONVERSATION DRAWER: everything said about one thing, opened from its
 * "Discuss" control anywhere in the workspace and addressed by
 * `?discuss=TYPE:id&thread=id`. Mounted once per project screen; renders nothing
 * until something asks for it.
 */
export function ConversationDrawer({ projectId }: { projectId: string }) {
  if (!useConversationsEnabled()) return null;
  return <OpenDrawer projectId={projectId} />;
}

function OpenDrawer({ projectId }: { projectId: string }) {
  const { discuss, threadId, openThread, close } = useDiscuss();
  if (discuss === null) return null;

  return (
    <DrawerFrame key={`${discuss.anchorType}:${discuss.anchorId}`} onClose={close}>
      <AnchorConversations
        projectId={projectId}
        target={discuss}
        threadId={threadId}
        onOpenThread={openThread}
        onClose={close}
      />
    </DrawerFrame>
  );
}

function DrawerFrame({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const modal = useModalFocus<HTMLDivElement>(onClose);
  return (
    <div
      data-testid="conversation-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Conversations"
      onKeyDown={modal.onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(35, 35, 35, 0.35)', zIndex: 45, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(560px, 100%)',
          height: '100%',
          overflowY: 'auto',
          borderLeft: '1px solid var(--color-divider)',
          boxShadow: '-12px 0 32px rgba(16, 24, 40, 0.12)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function AnchorConversations({
  projectId,
  target,
  threadId,
  onOpenThread,
  onClose,
}: {
  projectId: string;
  target: DiscussTarget;
  threadId: string | null;
  onOpenThread: (id: string | null) => void;
  onClose: () => void;
}) {
  const [starting, setStarting] = useState(false);
  const list = useQuery({
    queryKey: ['conversations', projectId, 'anchor', target.anchorType, target.anchorId],
    queryFn: () => api.conversations(projectId, { anchorType: target.anchorType, anchorId: target.anchorId }),
    refetchInterval: 30_000,
  });
  const carryover = useSceneCarryover(projectId);
  const placed =
    target.anchorType === 'SCENE'
      ? (carryover.data?.placed ?? []).filter((p) => p.sceneId === target.anchorId)
      : [];
  const certain = placed.filter((p) => p.verdict === 'CERTAIN').flatMap((p) => p.threads);
  const likely = placed.filter((p) => p.verdict === 'LIKELY').flatMap((p) => p.threads);

  const label = target.label ?? list.data?.threads[0]?.anchorLabel ?? `This ${ANCHOR_NOUN[target.anchorType]}`;

  return (
    <div style={{ display: 'grid', gap: 16, padding: '16px 20px 28px' }}>
      <header style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <p className="mm" style={{ margin: 0, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Conversations · {ANCHOR_NOUN[target.anchorType]}
          </p>
          <h2 style={{ margin: '4px 0 0', fontSize: 18, overflowWrap: 'anywhere' }} data-testid="conversation-drawer-anchor">
            {label}
          </h2>
        </div>
        <button type="button" className="ctl" data-testid="conversation-drawer-close" aria-label="Close conversations" onClick={onClose} style={{ minWidth: 36, minHeight: 36 }}>
          ✕
        </button>
      </header>

      {threadId ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <button type="button" className="ctl" data-testid="conversation-drawer-back" onClick={() => { onOpenThread(null); }}>
              ← All conversations here
            </button>
          </div>
          <ThreadView
            projectId={projectId}
            threadId={threadId}
            onClosed={() => {
              onOpenThread(null);
            }}
          />
        </div>
      ) : starting ? (
        <NewThreadForm
          projectId={projectId}
          anchorType={target.anchorType}
          anchorId={target.anchorId}
          onCancel={() => {
            setStarting(false);
          }}
          onCreated={(thread) => {
            setStarting(false);
            onOpenThread(thread.id);
          }}
        />
      ) : (
        <>
          <div>
            <button type="button" className="ctl" data-on="1" data-testid="conversation-drawer-start" onClick={() => { setStarting(true); }}>
              Start a conversation
            </button>
          </div>
          {list.isPending ? <p className="mm">Loading…</p> : null}
          {list.data ? (
            <ThreadList
              testId="conversation-drawer-list"
              threads={list.data.threads}
              showAnchor={false}
              emptyText={`Nobody has started a conversation about this ${ANCHOR_NOUN[target.anchorType]} yet.`}
              onOpen={onOpenThread}
            />
          ) : null}
          {certain.length > 0 ? (
            <section data-testid="conversation-drawer-earlier" style={{ display: 'grid', gap: 6 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>From an earlier draft of this scene</h3>
              <ThreadList testId="conversation-drawer-earlier-list" threads={certain} showAnchor emptyText="" onOpen={onOpenThread} />
            </section>
          ) : null}
          {likely.length > 0 ? (
            <section data-testid="conversation-drawer-possible" style={{ display: 'grid', gap: 6 }}>
              <h3 style={{ margin: 0, fontSize: 14 }}>Possibly the same scene</h3>
              <p className="mm" style={{ margin: 0, fontSize: 12.5 }}>
                These were started on an earlier draft, on a scene that looks like this one. Check before relying on them.
              </p>
              <ThreadList testId="conversation-drawer-possible-list" threads={likely} showAnchor emptyText="" onOpen={onOpenThread} />
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * A scene's "Discuss" control, counting the conversations CERTAINLY carried over
 * from an earlier draft onto this scene (spec §6); a LIKELY match is offered in
 * the drawer and never counted here.
 */
export function SceneConversationEntry({
  projectId,
  sceneId,
  label,
  compact = true,
}: {
  projectId: string;
  sceneId: string;
  label?: string;
  compact?: boolean;
}) {
  const carryover = useSceneCarryover(projectId);
  const certain = (carryover.data?.placed ?? [])
    .filter((p) => p.sceneId === sceneId && p.verdict === 'CERTAIN')
    .reduce((n, p) => n + p.threads.length, 0);
  return (
    <ConversationEntry
      projectId={projectId}
      anchorType="SCENE"
      anchorId={sceneId}
      label={label}
      extraThreads={certain}
      compact={compact}
    />
  );
}

/**
 * "Discussions on scenes not in this draft" (spec §6): scene conversations of an
 * earlier draft that no scene of this one can be placed on, so none of them ever
 * becomes unfindable. Renders nothing when there are none.
 */
export function UnplacedSceneConversations({ projectId }: { projectId: string }) {
  if (!useConversationsEnabled()) return null;
  return <UnplacedList projectId={projectId} />;
}

function UnplacedList({ projectId }: { projectId: string }) {
  const carryover = useSceneCarryover(projectId);
  const { open } = useDiscuss();
  const unplaced = carryover.data?.unplaced ?? [];
  if (unplaced.length === 0) return null;

  return (
    <section
      data-testid="conversations-unplaced"
      style={{ border: '1px solid var(--color-divider)', background: 'var(--color-bg)', padding: 12, display: 'grid', gap: 8, marginTop: 12 }}
    >
      <h3 style={{ margin: 0, fontSize: 14 }}>Discussions on scenes not in this draft</h3>
      <p className="mm" style={{ margin: 0, fontSize: 12.5 }}>
        These were started on a scene of an earlier draft that is not in this one, or not recognisably.
      </p>
      <ThreadList
        testId="conversations-unplaced-list"
        threads={unplaced}
        showAnchor
        emptyText=""
        onOpen={(id) => {
          const thread = unplaced.find((t) => t.id === id);
          if (thread) open({ anchorType: thread.anchorType, anchorId: thread.anchorId, label: thread.anchorLabel }, id);
        }}
      />
    </section>
  );
}
